import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ElementRef,
    HostListener,
} from '@angular/core';
import * as moment from 'moment';

export interface CalendarDay {
    date: moment.Moment;
    dayOfMonth: number;
    isCurrentMonth: boolean;
    isToday: boolean;
    key: string;
    label: string;
    disabled: boolean;
    selected: boolean;
    classes: { [key: string]: boolean };
}

/** Which level of the drill-down the calendar is showing. */
export type DatePickerView = 'days' | 'months' | 'years';

/** One cell of the month or year grid. Built up front so the template binds fields, not calls. */
export interface CalendarCell {
    /** 0-11 for a month, the year itself for a year. */
    value: number;
    label: string;
    disabled: boolean;
    selected: boolean;
    /** The month/year the grid is currently pointed at — outlined, the way today's date is. */
    current: boolean;
}

/**
 * Single-date picker that mirrors the look of vf-date-range-picker (same calendar grid,
 * popover and field trigger) but selects ONE date. Reusable anywhere a single date is needed
 * (e.g. custom Date fields on the project Details surface).
 */
@Component({
    selector: 'vf-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.css'],
})
export class DatePickerComponent implements OnInit, OnChanges {
    @Input() date: moment.Moment | string | undefined;
    @Input() placeholder: string = 'Select date';
    @Input() todayDate: moment.Moment;
    /**
     * Which edge of the trigger the popover hangs from.
     *
     * ⚠️ NOT called `align`. A static `align="right"` at a call site is written to the DOM as a real
     * `align` ATTRIBUTE on the host (Angular's `setUpAttributes` copies every static attribute across,
     * whether or not it also matches an `@Input`), and `align` is one of HTML's presentational
     * attributes — Blink's `HTMLElement` base maps it to `text-align` on ANY element, custom ones
     * included. Measured on `vf-date-picker`: the host and 16 descendants went `text-align: left` ->
     * `right` with no author rule anywhere to explain it. Nothing moved, because every text node in
     * here is a shrink-wrapped flex item or explicitly centred — but the next full-width label added
     * to this component would have silently drifted. See the same note on `vf-grid-column-display`,
     * where it *did* show. Never name an `@Input` after a presentational HTML attribute.
     */
    @Input() popoverAlign: 'left' | 'right' = 'left';
    /** Vertical open direction. 'down' (default) drops below the trigger; 'up' opens above it — for triggers
     *  near the bottom of a container (e.g. the completion pop-up) where a downward calendar would overflow. */
    @Input() dropDirection: 'down' | 'up' = 'down';
    /**
     * `field` / `minimal` render a trigger that opens the calendar in a popover. `inline` renders the
     * calendar itself with no trigger and no popover chrome — for callers that already own a surface to
     * put it on (the no-date nudge card, the Snooze menus), where a popover-inside-a-popover both looked
     * wrong and clipped against the menu's edge.
     */
    @Input() variant: 'field' | 'minimal' | 'inline' = 'field';
    /**
     * The right-hand footer button of the calendar. `clear` emits `undefined` (the field default),
     * `cancel` emits `cancelled` so an inline host can collapse the calendar without changing the date,
     * `none` hides it.
     */
    @Input() footerAction: 'clear' | 'cancel' | 'none' = 'clear';
    /**
     * Stretch the day grid across the host instead of drawing the fixed 32px cell track. Opt-in: the
     * popover and the Snooze menus size themselves to the track, while a caller that owns a wide
     * surface (the no-date nudge's 400px card) wants the calendar to fill it. `inline` only.
     */
    @Input() fluid: boolean = false;
    /**
     * The footer's `Today` link (default on). Off where today cannot be picked - e.g. the Won popup's reminder calendar,
     * which starts tomorrow, as its design draws it (Cancel only).
     */
    @Input() showToday: boolean = true;
    @Input() disabled: boolean = false;
    /** Optional lower bound: days before this (day granularity) are shown disabled + unselectable, and the
     *  month nav won't page to a month entirely before it. Undefined = no restriction (default), so existing
     *  callers are unaffected. Accepts a moment or any moment-parsable value. */
    @Input() minDate: moment.Moment | string | undefined;

    @Output() dateChange = new EventEmitter<moment.Moment | undefined>();
    /** `footerAction="cancel"` was clicked — the host should dismiss the calendar, date untouched. */
    @Output() cancelled = new EventEmitter<void>();

    @ViewChild('triggerEl', { static: false }) triggerEl: ElementRef;
    @ViewChild('popoverEl', { static: false }) popoverEl: ElementRef;

    isOpen = false;
    working: moment.Moment | undefined;
    displayedMonth: number = 0;
    displayedYear: number = 0;
    calendarWeeks: CalendarDay[][] = [];
    weekdays: string[] = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    /**
     * Drill-down: the header label is a button — days -> months -> years — so a birthday decades back
     * is three clicks instead of paging the month arrow hundreds of times (asked for 2026-09-18).
     * Picking a year drops to that year's months, picking a month drops to its days.
     */
    view: DatePickerView = 'days';
    monthCells: CalendarCell[] = [];
    yearCells: CalendarCell[] = [];
    /** First year of the visible 12-year page. */
    private yearPageStart: number = 0;
    private static readonly YEARS_PER_PAGE: number = 12;

    private openingEvent: MouseEvent | null = null;

    ngOnInit(): void {
        this.todayDate = this.ensureMoment(this.todayDate) || moment().startOf('day');
        if (this.isInline) {
            this.resetToDate();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.todayDate) {
            this.todayDate = this.ensureMoment(this.todayDate) || moment().startOf('day');
        }
        if (changes.date && !this.isOpen) {
            this.working = this.ensureMoment(this.date);
        }
        // Inline has no open/close cycle to rebuild the grid on, so any input that moves the grid
        // (the bound date, "today", the lower bound) has to rebuild it here.
        if (this.isInline && (changes.date || changes.todayDate || changes.minDate || changes.variant)) {
            this.resetToDate();
        }
    }

    /** True while the calendar is rendered directly on the host's surface (no trigger, no popover). */
    get isInline(): boolean {
        return this.variant === 'inline';
    }

    /** True while the calendar body should be in the DOM — always for inline, only when open otherwise. */
    get isCalendarVisible(): boolean {
        return this.isInline || this.isOpen;
    }

    /** Point the grid at the bound date (or today) and build it. */
    private resetToDate(): void {
        let nav: moment.Moment = null;
        this.working = this.ensureMoment(this.date);
        nav = this.working || this.todayDate || moment();
        this.displayedMonth = nav.month();
        this.displayedYear = nav.year();
        this.view = 'days';
        this.buildCalendarWeeks();
    }

    private ensureMoment(val: any): moment.Moment | undefined {
        let m: moment.Moment = null;
        if (!val) {
            return undefined;
        }
        if (moment.isMoment(val)) {
            return moment(val.format('YYYY-MM-DD'), 'YYYY-MM-DD');
        }
        m = moment(val);
        return m.isValid() ? moment(m.format('YYYY-MM-DD'), 'YYYY-MM-DD') : undefined;
    }

    /** Normalized lower bound (day-granularity moment), or undefined when unset. */
    private get minMoment(): moment.Moment | undefined {
        return this.ensureMoment(this.minDate);
    }

    /** A current-month day that falls before minDate - disabled + unselectable. */
    isDisabledDay(day: CalendarDay): boolean {
        let min = this.minMoment;
        return !!(min && day.isCurrentMonth && day.date.isBefore(min, 'day'));
    }

    /** False when everything the prev arrow would page to is before minDate. Per view: the previous
     *  month, the previous year, or the previous 12-year page. */
    get canGoPrev(): boolean {
        let min = this.minMoment;
        if (!min) {
            return true;
        }
        if (this.view === 'months') {
            return moment({ year: this.displayedYear, month: 0, day: 1 }).isAfter(min, 'year');
        }
        if (this.view === 'years') {
            return this.yearPageStart > min.year();
        }
        return moment({ year: this.displayedYear, month: this.displayedMonth, day: 1 }).isAfter(min, 'month');
    }

    get hasDate(): boolean {
        return !!this.ensureMoment(this.date);
    }

    get triggerText(): string {
        let d = this.ensureMoment(this.date);
        return d ? d.format('MMM D, YYYY') : '';
    }

    get monthYearLabel(): string {
        return moment({ year: this.displayedYear, month: this.displayedMonth }).format('MMMM YYYY');
    }

    /** What the header button reads at this level. */
    get headerLabel(): string {
        if (this.view === 'months') {
            return String(this.displayedYear);
        }
        if (this.view === 'years') {
            return this.yearPageStart + ' – ' + (this.yearPageStart + DatePickerComponent.YEARS_PER_PAGE - 1);
        }
        return this.monthYearLabel;
    }

    /** The header button: days -> months -> years, and years steps back down to months. */
    onHeaderClick(): void {
        if (this.view === 'days') {
            this.showMonths();
            return;
        }
        if (this.view === 'months') {
            this.showYears();
            return;
        }
        this.showMonths();
    }

    showMonths(): void {
        this.view = 'months';
        this.buildMonthCells();
    }

    showYears(): void {
        this.view = 'years';
        // Page the grid so the year in view sits on it, rather than always starting at a fixed epoch.
        this.yearPageStart =
            this.displayedYear -
            (((this.displayedYear % DatePickerComponent.YEARS_PER_PAGE) + DatePickerComponent.YEARS_PER_PAGE) %
                DatePickerComponent.YEARS_PER_PAGE);
        this.buildYearCells();
    }

    /** Year picked -> that year's months. */
    selectYear(cell: CalendarCell): void {
        if (cell.disabled) {
            return;
        }
        this.displayedYear = cell.value;
        this.showMonths();
    }

    /** Month picked -> that month's days, which is where a date actually gets chosen. */
    selectMonth(cell: CalendarCell): void {
        if (cell.disabled) {
            return;
        }
        this.displayedMonth = cell.value;
        this.view = 'days';
        this.buildCalendarWeeks();
    }

    /** The prev/next arrows, at whatever level is showing. */
    prev(): void {
        if (!this.canGoPrev) {
            return;
        }
        if (this.view === 'months') {
            this.displayedYear--;
            this.buildMonthCells();
            return;
        }
        if (this.view === 'years') {
            this.yearPageStart -= DatePickerComponent.YEARS_PER_PAGE;
            this.buildYearCells();
            return;
        }
        this.prevMonth();
    }

    next(): void {
        if (this.view === 'months') {
            this.displayedYear++;
            this.buildMonthCells();
            return;
        }
        if (this.view === 'years') {
            this.yearPageStart += DatePickerComponent.YEARS_PER_PAGE;
            this.buildYearCells();
            return;
        }
        this.nextMonth();
    }

    private buildMonthCells(): void {
        const min: moment.Moment | undefined = this.minMoment;
        const today: moment.Moment = this.todayDate || moment().startOf('day');
        const cells: CalendarCell[] = [];
        let i: number = 0;

        for (i = 0; i < 12; i++) {
            const start: moment.Moment = moment({ year: this.displayedYear, month: i, day: 1 });
            cells.push({
                value: i,
                label: start.format('MMM'),
                // A month is out of reach only when its LAST day is before the bound.
                disabled: !!(min && start.clone().endOf('month').isBefore(min, 'day')),
                selected: !!(this.working && this.working.year() === this.displayedYear && this.working.month() === i),
                current: today.year() === this.displayedYear && today.month() === i,
            });
        }
        this.monthCells = cells;
    }

    private buildYearCells(): void {
        const min: moment.Moment | undefined = this.minMoment;
        const today: moment.Moment = this.todayDate || moment().startOf('day');
        const cells: CalendarCell[] = [];
        let i: number = 0;
        let year: number = 0;

        for (i = 0; i < DatePickerComponent.YEARS_PER_PAGE; i++) {
            year = this.yearPageStart + i;
            cells.push({
                value: year,
                label: String(year),
                disabled: !!(min && year < min.year()),
                selected: !!(this.working && this.working.year() === year),
                current: today.year() === year,
            });
        }
        this.yearCells = cells;
    }

    open(event: MouseEvent): void {
        let nav: moment.Moment = null;
        if (this.disabled) {
            return;
        }
        if (this.isOpen) {
            this.dismiss();
            return;
        }
        this.openingEvent = event;
        this.working = this.ensureMoment(this.date);
        nav = this.working || this.todayDate || moment();
        this.displayedMonth = nav.month();
        this.displayedYear = nav.year();
        // Always open on the days grid, whatever level the last visit left behind.
        this.view = 'days';
        this.buildCalendarWeeks();
        this.isOpen = true;
    }

    dismiss(): void {
        this.isOpen = false;
    }

    onDayClick(day: CalendarDay): void {
        if (!day.isCurrentMonth || this.isDisabledDay(day)) {
            return;
        }
        this.working = day.date.clone();
        this.buildCalendarWeeks();
        this.dateChange.emit(this.working.clone().utc(true));
        this.isOpen = false;
    }

    selectToday(): void {
        let t = this.todayDate || moment().startOf('day');
        let min = this.minMoment;
        if (min && t.isBefore(min, 'day')) {
            return; // today is before the allowed minimum
        }
        this.working = t.clone();
        this.displayedMonth = t.month();
        this.displayedYear = t.year();
        this.view = 'days';
        this.buildCalendarWeeks();
        this.dateChange.emit(this.working.clone().utc(true));
        this.isOpen = false;
    }

    clearAndEmit(): void {
        this.working = undefined;
        this.buildCalendarWeeks();
        this.dateChange.emit(undefined);
        this.isOpen = false;
    }

    /** `footerAction="cancel"` — leave the date alone and let the host collapse the calendar. */
    cancel(): void {
        this.isOpen = false;
        this.cancelled.emit();
    }

    clearInline(event: MouseEvent): void {
        event.stopPropagation();
        this.working = undefined;
        this.dateChange.emit(undefined);
    }

    prevMonth(): void {
        if (!this.canGoPrev) {
            return;
        }
        this.displayedMonth--;
        if (this.displayedMonth < 0) {
            this.displayedMonth = 11;
            this.displayedYear--;
        }
        this.buildCalendarWeeks();
    }

    nextMonth(): void {
        this.displayedMonth++;
        if (this.displayedMonth > 11) {
            this.displayedMonth = 0;
            this.displayedYear++;
        }
        this.buildCalendarWeeks();
    }

    buildCalendarWeeks(): void {
        let firstOfMonth = moment({ year: this.displayedYear, month: this.displayedMonth, day: 1 });
        let startOfGrid = firstOfMonth.clone().startOf('week');
        let today = this.todayDate || moment().startOf('day');
        let weeks: CalendarDay[][] = [];
        let current = startOfGrid.clone();
        let w = 0;
        let d = 0;

        for (w = 0; w < 6; w++) {
            let week: CalendarDay[] = [];
            for (d = 0; d < 7; d++) {
                const day: CalendarDay = {
                    date: current.clone(),
                    dayOfMonth: current.date(),
                    isCurrentMonth: current.month() === this.displayedMonth,
                    isToday: current.isSame(today, 'day'),
                    key: current.format('YYYY-MM-DD'),
                    label: current.format('MMMM D, YYYY'),
                    disabled: false,
                    selected: !!this.working && current.isSame(this.working, 'day'),
                    classes: {},
                };
                day.disabled = !day.isCurrentMonth || this.isDisabledDay(day);
                day.classes = this.getDayClasses(day);
                week.push(day);
                current.add(1, 'day');
            }
            if (week.some((x) => x.isCurrentMonth)) {
                weeks.push(week);
            }
        }
        this.calendarWeeks = weeks;
    }

    trackWeek(_index: number, week: CalendarDay[]): string {
        return week[0].key;
    }

    trackDay(_index: number, day: CalendarDay): string {
        return day.key;
    }

    trackCell(_index: number, cell: CalendarCell): number {
        return cell.value;
    }

    getDayClasses(day: CalendarDay): { [key: string]: boolean } {
        let isSelected = false;
        if (!day.isCurrentMonth) {
            return { 'vf-dp-day--outside': true };
        }
        if (this.isDisabledDay(day)) {
            return { 'vf-dp-day--disabled': true };
        }
        isSelected = !!(this.working && day.date.isSame(this.working, 'day'));
        return {
            'vf-dp-day--today': day.isToday && !isSelected,
            'vf-dp-day--selected': isSelected,
        };
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        let target: HTMLElement = null;
        if (!this.isOpen) {
            return;
        }
        if (this.openingEvent && event === this.openingEvent) {
            this.openingEvent = null;
            return;
        }
        this.openingEvent = null;
        target = event.target as HTMLElement;
        if (this.triggerEl && this.triggerEl.nativeElement.contains(target)) {
            return;
        }
        if (this.popoverEl && this.popoverEl.nativeElement.contains(target)) {
            return;
        }
        this.dismiss();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.isOpen) {
            this.dismiss();
        }
    }
}
